/**
 * Tabelle fuer die drei regionalen Auswertungen (Inlandsversand, Versandstatistik,
 * Gebietsaufnahme) und fuer Zementverwendung -- ein gemeinsames Tabellenmuster nach Sketch 023
 * Variante C, Runde 7 (D-04/D-05/D-07). Rendert ausschliesslich, was der Server in `data.table`
 * und `data.cells` liefert: keine Summen, Anteile oder Aenderungen entstehen im Browser.
 *
 * Optik wie die Unfallauswertung (components/ui/reportTable): Mantine-`Table`, zweizeiliger Kopf,
 * Gruppengrenzen, Gesamtzeile gelb, Zeilen-Hover ueber `--row-bg`. Zellen mit Sperrzustand tragen
 * ihren eigenen Hintergrund und lesen `--row-bg` nicht -- der Hover ueberdeckt sie deshalb nie.
 */
import {
  Fragment,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Card, Group, Table, Text, Title } from '@mantine/core';
import { CellLockMenu } from './CellLockMenu';
import { LockLegend } from './LockLegend';
import {
  EvaluationValueCell,
  PreviousValueCell,
  type EvaluationCellUnit,
} from './EvaluationValueCell';
import { cellColorVars } from './evaluationColorVars';
import { formatChange, formatShare, formatTonnes } from './evaluationFormat';
import { getDeltaColor } from '../../utils/formatting';
import {
  DIVIDER_STYLE,
  GROUP_HEADER_TYPO,
  TABLE_CARD_STYLE,
  HEADER_GROUP_BG,
  HEADER_LABEL_BG,
  rowStyle,
} from '../ui/reportTable';
import rowClasses from '../ui/reportTable.module.css';
import type {
  CellCompliance,
  CellInfo,
  EvaluationTableResponse,
  RegionalTable,
  ZementverwendungTable,
} from '../../types/evaluationTable';
import classes from './evaluationTable.module.css';

function cx(...values: Array<string | false | undefined>): string {
  return values.filter(Boolean).join(' ');
}

/** Veraenderung in Prozent wie die Delta-Spalten der Unfallauswertung (fz xs, getDeltaColor). */
export function ChangeCell({
  value,
  style,
}: {
  value: string | null;
  style?: CSSProperties;
}) {
  const num = value === null ? null : Number(value);
  return (
    <Table.Td className={classes.num} style={style}>
      <Text component="span" fz="xs" fw={500} c={getDeltaColor(num)}>
        {formatChange(value)}
      </Text>
    </Table.Td>
  );
}

/** Ueberschrift ueber der Tabelle: Titel und Meldestand. */
export function TableCaption({
  caption,
  data,
}: {
  caption: string;
  data: EvaluationTableResponse;
}) {
  return (
    <Group gap="sm" align="baseline" mb="sm">
      <Title order={2} fz="h4" fw={700}>
        {caption}
      </Title>
      <Text fz="sm" c="vdzGray.6">
        <Text component="span" fz="sm" fw={700} c="vdzGray.8">
          {data.reported.reported} von {data.reported.expected}
        </Text>{' '}
        Werken genehmigt
        {data.reported.reported < data.reported.expected
          ? ' – die Zahlen können sich noch ändern'
          : ''}
      </Text>
    </Group>
  );
}

/** Karte ohne Innenabstand wie in der Unfallauswertung: die Tabelle reicht bis an den Rand. */
export function TableCard({ children }: { children: ReactNode }) {
  return (
    <Card padding={0} radius="md" withBorder style={TABLE_CARD_STYLE}>
      {children}
    </Card>
  );
}

function handleRowKeyDown(
  event: KeyboardEvent<HTMLElement>,
  onActivate: () => void
) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onActivate();
  }
}

/** Zeilenname (Td wie die Namen der Unfallauswertung -- Th traegt den Kopfzellen-Stil des Themes);
 *  oeffnet die Aufschluesselung, wenn der Server einen Schluessel dafuer liefert (D-08). */
export function RowLabel({
  breakdownKey,
  onOpenBreakdown,
  title,
  children,
}: {
  breakdownKey: string | null;
  onOpenBreakdown: (rowKey: string) => void;
  title?: string;
  children: ReactNode;
}) {
  const clickable = breakdownKey !== null;
  const open = () => {
    if (breakdownKey) {
      onOpenBreakdown(breakdownKey);
    }
  };
  return (
    <Table.Td
      title={title}
      className={cx(classes.labelCol, clickable && classes.labelClickable)}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? open : undefined}
      onKeyDown={
        clickable ? (event) => handleRowKeyDown(event, open) : undefined
      }
    >
      {children}
    </Table.Td>
  );
}

const GROUP_HEAD_STYLE: CSSProperties = {
  backgroundColor: HEADER_GROUP_BG,
  textAlign: 'center',
  ...GROUP_HEADER_TYPO,
};

const LABEL_HEAD_STYLE: CSSProperties = {
  backgroundColor: HEADER_LABEL_BG,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

/** Gemeinsame Props aller Tabellenkomponenten dieser Phase (RegionalEvaluationTable, ZementartenMatrix). */
export interface EvaluationTableSharedProps {
  data: EvaluationTableResponse;
  openCellKey: string | null;
  onOpenCell: (cellKey: string) => void;
  onCloseCell: () => void;
  highlight: ReadonlySet<string>;
  onChanged: () => void;
  onOpenBreakdown: (rowKey: string) => void;
}

interface ValueCellWithMenuProps extends EvaluationTableSharedProps {
  cell: CellInfo;
  unit: EvaluationCellUnit;
  bold?: boolean;
  zeroMuted?: boolean;
  style?: CSSProperties;
}

/**
 * Wertzelle + Klick-Menue (D-14): gemeinsame Verdrahtung fuer RegionalEvaluationTable und
 * ZementartenMatrix, damit "genau ein Menue offen" (openCellKey) nur an einer Stelle entsteht.
 * Das Menue haengt an einem unsichtbaren Anker innerhalb der Zelle -- die Zelle selbst bleibt der
 * eigentliche Klickbereich (EvaluationValueCell.onOpen).
 */
export function ValueCellWithMenu({
  data,
  cell,
  unit,
  openCellKey,
  onOpenCell,
  onCloseCell,
  highlight,
  onChanged,
  onOpenBreakdown,
  bold = false,
  zeroMuted = false,
  style,
}: ValueCellWithMenuProps) {
  const opened = openCellKey === cell.cellKey;
  return (
    <EvaluationValueCell
      cell={cell}
      unit={unit}
      bold={bold}
      zeroMuted={zeroMuted}
      style={style}
      highlighted={highlight.has(cell.cellKey)}
      onOpen={(cellKey) => onOpenCell(cellKey)}
      menu={
        opened ? (
          <CellLockMenu
            opened
            onClose={onCloseCell}
            target={<span aria-hidden="true" />}
            cell={cell}
            unit={unit}
            evaluationType={data.evaluationType}
            year={data.year}
            period={data.period}
            editable={data.editable}
            onChanged={onChanged}
            onOpenBreakdown={onOpenBreakdown}
          />
        ) : undefined
      }
    />
  );
}

/** Die drei Compliance-Spalten (Werke, Unternehmen, größter Anteil) -- '–', wenn compliance null ist. */
function ComplianceCells({
  compliance,
}: {
  compliance: CellCompliance | null;
}) {
  if (!compliance) {
    return (
      <>
        <Table.Td className={classes.num} style={DIVIDER_STYLE}>
          –
        </Table.Td>
        <Table.Td className={classes.num}>–</Table.Td>
        <Table.Td className={classes.num}>–</Table.Td>
      </>
    );
  }
  const companiesBad = compliance.companies < 5;
  const shareBad = Number(compliance.maxShare) >= 80;
  return (
    <>
      <Table.Td className={classes.num} style={DIVIDER_STYLE}>
        {compliance.plants}
      </Table.Td>
      <Table.Td
        className={cx(classes.num, companiesBad && classes.complianceBad)}
      >
        {compliance.companies}
      </Table.Td>
      <Table.Td className={cx(classes.num, shareBad && classes.complianceBad)}>
        {formatShare(compliance.maxShare)}
      </Table.Td>
    </>
  );
}

function RegionalRows({
  table,
  shared,
}: {
  table: RegionalTable;
  shared: EvaluationTableSharedProps;
}) {
  const { data, onOpenBreakdown } = shared;
  const showCumulative = !table.single && table.cumulativeGroupLabel !== null;

  return (
    <>
      <Table.Thead>
        <Table.Tr>
          <Table.Th style={{ ...GROUP_HEAD_STYLE, textAlign: 'left' }} />
          <Table.Th colSpan={3} style={GROUP_HEAD_STYLE}>
            {table.periodGroupLabel}
          </Table.Th>
          {showCumulative && (
            <Table.Th
              colSpan={3}
              style={{ ...GROUP_HEAD_STYLE, ...DIVIDER_STYLE }}
            >
              {table.cumulativeGroupLabel}
            </Table.Th>
          )}
          <Table.Th
            colSpan={3}
            style={{ ...GROUP_HEAD_STYLE, ...DIVIDER_STYLE }}
          >
            Compliance
          </Table.Th>
        </Table.Tr>
        <Table.Tr>
          <Table.Th style={{ ...LABEL_HEAD_STYLE, textAlign: 'left' }}>
            {table.rowHeader}
          </Table.Th>
          <Table.Th style={LABEL_HEAD_STYLE}>{data.year}</Table.Th>
          <Table.Th style={LABEL_HEAD_STYLE}>{data.year - 1}</Table.Th>
          <Table.Th style={LABEL_HEAD_STYLE}>Veränd. %</Table.Th>
          {showCumulative && (
            <>
              <Table.Th style={{ ...LABEL_HEAD_STYLE, ...DIVIDER_STYLE }}>
                {data.year}
              </Table.Th>
              <Table.Th style={LABEL_HEAD_STYLE}>{data.year - 1}</Table.Th>
              <Table.Th style={LABEL_HEAD_STYLE}>Veränd. %</Table.Th>
            </>
          )}
          <Table.Th style={{ ...LABEL_HEAD_STYLE, ...DIVIDER_STYLE }}>
            Werke
          </Table.Th>
          <Table.Th style={LABEL_HEAD_STYLE}>Unternehmen</Table.Th>
          <Table.Th style={LABEL_HEAD_STYLE}>größter Anteil</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {table.rows.map((row) => {
          const cell = data.cells[row.cellKey];
          const sum = row.kind !== 'detail';
          return (
            <Table.Tr
              key={row.rowKey}
              className={rowClasses.dataRow}
              style={rowStyle(row.kind === 'grand', row.firstSum)}
              fw={sum ? 700 : undefined}
            >
              <RowLabel
                breakdownKey={cell?.breakdownRowKey ?? null}
                onOpenBreakdown={onOpenBreakdown}
              >
                {row.label}
              </RowLabel>
              <ValueCellWithMenu
                {...shared}
                cell={cell}
                unit="tonnes"
                bold={sum}
              />
              <PreviousValueCell
                value={cell.previousValue}
                previousLocked={cell.previousLocked}
                unit="tonnes"
              />
              <ChangeCell value={row.change} />
              {showCumulative && (
                <>
                  <Table.Td className={classes.num} style={DIVIDER_STYLE}>
                    {formatTonnes(row.cumulative)}
                  </Table.Td>
                  <Table.Td className={classes.num}>
                    {formatTonnes(row.cumulativePrevious)}
                  </Table.Td>
                  <ChangeCell value={row.cumulativeChange} />
                </>
              )}
              <ComplianceCells compliance={row.compliance} />
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </>
  );
}

function ZementverwendungRows({
  table,
  shared,
}: {
  table: ZementverwendungTable;
  shared: EvaluationTableSharedProps;
}) {
  const { data, onOpenBreakdown } = shared;

  return (
    <>
      <Table.Thead>
        <Table.Tr>
          <Table.Th style={{ ...GROUP_HEAD_STYLE, textAlign: 'left' }} />
          {table.categories.map((category, index) => (
            <Table.Th
              key={category.field}
              colSpan={2}
              style={{
                ...GROUP_HEAD_STYLE,
                ...(index > 0 ? DIVIDER_STYLE : {}),
              }}
            >
              {category.label}
            </Table.Th>
          ))}
          <Table.Th
            colSpan={3}
            style={{ ...GROUP_HEAD_STYLE, ...DIVIDER_STYLE }}
          >
            Compliance
          </Table.Th>
        </Table.Tr>
        <Table.Tr>
          <Table.Th style={{ ...LABEL_HEAD_STYLE, textAlign: 'left' }}>
            {table.rowHeader}
          </Table.Th>
          {table.categories.map((category, index) => (
            <Fragment key={category.field}>
              <Table.Th
                style={{
                  ...LABEL_HEAD_STYLE,
                  ...(index > 0 ? DIVIDER_STYLE : {}),
                }}
              >
                {data.year}
              </Table.Th>
              <Table.Th style={LABEL_HEAD_STYLE}>{data.year - 1}</Table.Th>
            </Fragment>
          ))}
          <Table.Th style={{ ...LABEL_HEAD_STYLE, ...DIVIDER_STYLE }}>
            Werke
          </Table.Th>
          <Table.Th style={LABEL_HEAD_STYLE}>Unternehmen</Table.Th>
          <Table.Th style={LABEL_HEAD_STYLE}>größter Anteil</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {table.rows.map((row) => {
          const firstCell = data.cells[row.cellKeys[0]];
          const sum = row.kind !== 'detail';
          return (
            <Table.Tr
              key={row.rowKey}
              className={rowClasses.dataRow}
              style={rowStyle(row.kind === 'grand', row.firstSum)}
              fw={sum ? 700 : undefined}
            >
              <RowLabel
                breakdownKey={firstCell?.breakdownRowKey ?? null}
                onOpenBreakdown={onOpenBreakdown}
              >
                {row.label}
              </RowLabel>
              {row.cellKeys.map((cellKey, index) => {
                const cell = data.cells[cellKey];
                return (
                  <Fragment key={cellKey}>
                    <ValueCellWithMenu
                      {...shared}
                      cell={cell}
                      unit="percent"
                      bold={sum}
                      style={index > 0 ? DIVIDER_STYLE : undefined}
                    />
                    <PreviousValueCell
                      value={cell.previousValue}
                      previousLocked={cell.previousLocked}
                      unit="percent"
                    />
                  </Fragment>
                );
              })}
              <ComplianceCells compliance={row.compliance} />
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </>
  );
}

export function RegionalEvaluationTable(props: EvaluationTableSharedProps) {
  const { data } = props;
  const table = data.table;
  if (!table) {
    return null;
  }
  if (table.layout !== 'regional' && table.layout !== 'zementverwendung') {
    return null;
  }

  return (
    <div style={cellColorVars}>
      <TableCaption caption={table.caption} data={data} />
      <TableCard>
        <div data-testid="evaluation-table-scroll" className={classes.scroll}>
          <Table data-testid="evaluation-table" fz="sm">
            {table.layout === 'regional' ? (
              <RegionalRows table={table} shared={props} />
            ) : (
              <ZementverwendungRows table={table} shared={props} />
            )}
          </Table>
        </div>
      </TableCard>
      <LockLegend />
    </div>
  );
}
